import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FormChips } from "@/pages/sports/components/form-chips";
import { TeamLink } from "@/pages/sports/components/team-link";
import { fmtCount, fmtFraction } from "@/pages/sports/lib/format";
import type { IStandingsRow } from "@/types/league-standings";

const signed = (diff?: number | null) =>
  typeof diff !== "number" ? "—" : diff > 0 ? `+${diff}` : String(diff);

/**
 * One league table. Columns the sport does not use are left out rather than shown
 * empty: no draws (basketball, volleyball), no points (basketball), no win % (football).
 * For / Against are goals in football and points in basketball and volleyball.
 */
export function StandingsTable({ rows }: { rows: IStandingsRow[] }) {
  const showDrawn = rows.some((r) => r.drawn != null);
  const showPoints = rows.some((r) => r.points != null);
  const showWinPct = rows.some((r) => r.winPct != null);
  const num = "text-right tabular-nums";

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">#</TableHead>
          <TableHead>Team</TableHead>
          <TableHead className={num} title="Played">
            P
          </TableHead>
          <TableHead className={num} title="Won">
            W
          </TableHead>
          {showDrawn && (
            <TableHead className={num} title="Drawn">
              D
            </TableHead>
          )}
          <TableHead className={num} title="Lost">
            L
          </TableHead>
          <TableHead className={num}>For</TableHead>
          <TableHead className={num}>Against</TableHead>
          <TableHead className={num} title="Difference">
            Diff
          </TableHead>
          {showPoints && (
            <TableHead className={num} title="Points">
              Pts
            </TableHead>
          )}
          {showWinPct && <TableHead className={num}>Win %</TableHead>}
          <TableHead>Form</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={row.team?.id ?? i}>
            <TableCell className="tabular-nums text-muted-foreground">
              {fmtCount(row.rank)}
            </TableCell>
            <TableCell>
              <TeamLink team={row.team} />
            </TableCell>
            <TableCell className={num}>{fmtCount(row.played)}</TableCell>
            <TableCell className={num}>{fmtCount(row.won)}</TableCell>
            {showDrawn && (
              <TableCell className={num}>{fmtCount(row.drawn)}</TableCell>
            )}
            <TableCell className={num}>{fmtCount(row.lost)}</TableCell>
            <TableCell className={num}>{fmtCount(row.scoredFor)}</TableCell>
            <TableCell className={num}>{fmtCount(row.scoredAgainst)}</TableCell>
            <TableCell className={num}>{signed(row.diff)}</TableCell>
            {showPoints && (
              <TableCell className={`${num} font-semibold`}>
                {fmtCount(row.points)}
              </TableCell>
            )}
            {showWinPct && (
              <TableCell className={num}>{fmtFraction(row.winPct)}</TableCell>
            )}
            <TableCell>
              <FormChips form={row.form} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
